const WishlistStore = (function () {
  const loggedIn = Boolean(window.wishlistState && window.wishlistState.loggedIn);
  let cache = Array.isArray(window.wishlistState && window.wishlistState.initialHandles)
    ? window.wishlistState.initialHandles.slice()
    : [];

  function notify() {
    publish(PUB_SUB_EVENTS.wishlistUpdate, { handles: cache.slice() });
  }

  // Kolejka zapewnia, że żądania do serwera lecą po kolei, w kolejności
  // kliknięć - bez niej szybki add->remove tego samego (lub innego)
  // produktu mógłby wysłać dwa równoległe read-modify-write i zgubić jedną
  // ze zmian w metapolu.
  let syncQueue = Promise.resolve();

  function syncToServer(action, handle) {
    syncQueue = syncQueue.then(() =>
      fetch("/apps/wishlist/sync", {
        ...fetchConfig("json"),
        body: JSON.stringify({ action, handle }),
      })
        .then((r) => {
          if (!r.ok) throw new Error(`sync failed: ${r.status}`);
          return r.json();
        })
        .then((data) => {
          if (Array.isArray(data.handles)) {
            cache = data.handles;
            notify();
          }
        })
        .catch(() => {
          // Zapis się nie udał - cofnij optymistyczną zmianę do stanu sprzed akcji.
          const hasHandle = cache.indexOf(handle) !== -1;
          if (action === "add" && hasHandle) {
            cache = cache.filter((h) => h !== handle);
          } else if (action === "remove" && !hasHandle) {
            cache = [...cache, handle];
          }
          notify();
        })
    );
  }

  return {
    isLoggedIn() {
      return loggedIn;
    },
    getAll() {
      return cache.slice();
    },
    has(handle) {
      return cache.indexOf(handle) !== -1;
    },
    toggle(handle) {
      if (!loggedIn) return null;
      const wasActive = cache.indexOf(handle) !== -1;
      const action = wasActive ? "remove" : "add";
      cache = wasActive ? cache.filter((h) => h !== handle) : [...cache, handle];
      notify();
      syncToServer(action, handle);
      return !wasActive;
    },
  };
})();

if (!customElements.get("favorite-button")) {
  customElements.define(
    "favorite-button",
    class FavoriteButton extends HTMLElement {
      connectedCallback() {
        this.handle = this.dataset.productHandle;
        this.button = this.querySelector("button");
        this.render();
        // Na stronie produktu <two-column-layout> przenosi bloki między
        // kolumnami, więc ten element zostaje odpięty i podpięty ponownie -
        // connectedCallback leci drugi raz. Handlery trzymamy w polach, żeby
        // addEventListener dostał tę samą referencję i zignorował duplikat;
        // bez tego jedno kliknięcie robiło add + remove (czyli nic).
        if (this._unsubscribe) this._unsubscribe();
        this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () =>
          this.render()
        );
        this._onClick = this._onClick || this.onClick.bind(this);
        this._onAnimationEnd =
          this._onAnimationEnd ||
          (() => this.button.classList.remove("is-beating"));
        this.button.addEventListener("click", this._onClick);
        this.button.addEventListener("animationend", this._onAnimationEnd);
      }

      disconnectedCallback() {
        if (this._unsubscribe) {
          this._unsubscribe();
          this._unsubscribe = null;
        }
      }

      onClick(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (!WishlistStore.isLoggedIn()) {
          const modal = document.querySelector("#WishlistLoginModal");
          if (modal) modal.show(this.button);
          return;
        }
        const active = WishlistStore.toggle(this.handle);
        if (active) this.beat();
      }

      beat() {
        this.button.classList.remove("is-beating");
        // force reflow so the animation restarts on rapid re-clicks
        void this.button.offsetWidth;
        this.button.classList.add("is-beating");
      }

      render() {
        const active = WishlistStore.isLoggedIn() && WishlistStore.has(this.handle);
        this.button.classList.toggle("is-active", active);
        this.button.setAttribute("aria-pressed", active ? "true" : "false");
        const label = active
          ? this.dataset.labelRemove
          : this.dataset.labelAdd;
        if (label) {
          const visuallyHidden = this.button.querySelector(
            ".visually-hidden"
          );
          if (visuallyHidden) visuallyHidden.textContent = label;
        }
      }
    }
  );
}

function createFavoriteButtonElement(handle, labelAdd, labelRemove) {
  const el = document.createElement("favorite-button");
  el.className = "favorite-button";
  el.dataset.productHandle = handle;
  if (labelAdd) el.dataset.labelAdd = labelAdd;
  if (labelRemove) el.dataset.labelRemove = labelRemove;

  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("aria-pressed", "false");

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("stroke-width", "1.5");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.setAttribute("class", "icon icon-favorite");
  svg.setAttribute("aria-hidden", "true");

  const path = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "path"
  );
  path.setAttribute(
    "d",
    "M20.84,4.61 a5.5,5.5 0 0 0 -7.78,0 L12,5.67 l-1.06,-1.06 a5.5,5.5 0 0 0 -7.78,7.78 l1.06,1.06 L12,21.23 l7.78,-7.78 l1.06,-1.06 a5.5,5.5 0 0 0 0,-7.78 Z"
  );
  path.setAttribute(
    "style",
    "stroke:currentColor;stroke-linecap:round;stroke-linejoin:round;"
  );
  svg.appendChild(path);
  button.appendChild(svg);

  const srLabel = document.createElement("span");
  srLabel.className = "visually-hidden";
  srLabel.textContent = labelAdd || "";
  button.appendChild(srLabel);

  el.appendChild(button);
  return el;
}

if (!customElements.get("favorites-list")) {
  customElements.define(
    "favorites-list",
    class FavoritesList extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector(".favorites-list__grid");
        this.emptyState = this.querySelector(".favorites-list__empty");
        this.loginRequiredState = this.querySelector(".favorites-list__login-required");
        this.countEl = this.querySelector(".favorites-list__count");
        this.addAllButton = this.querySelector(".favorites-list__add-all");
        this._products = [];
        // To samo zabezpieczenie co w favorite-button - odporność na
        // ponowne podpięcie elementu przez motyw.
        if (this._unsubscribe) this._unsubscribe();
        this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () =>
          this.load()
        );
        if (this.addAllButton) {
          this._onAddAll = this._onAddAll || this.addAllToCart.bind(this);
          this.addAllButton.addEventListener("click", this._onAddAll);
        }
        this.load();
      }

      disconnectedCallback() {
        if (this._unsubscribe) {
          this._unsubscribe();
          this._unsubscribe = null;
        }
      }

      formatMoney(cents) {
        return (cents / 100).toLocaleString(undefined, {
          style: "currency",
          currency: Shopify.currency.active,
        });
      }

      updateCount(count) {
        if (this.countEl && this.dataset.labelCount) {
          this.countEl.textContent = this.dataset.labelCount.replace(
            "%COUNT%",
            count
          );
        }
        if (this.addAllButton) this.addAllButton.hidden = count === 0;
      }

      load() {
        if (!WishlistStore.isLoggedIn()) {
          this.grid.hidden = true;
          this.emptyState.hidden = true;
          if (this.loginRequiredState) this.loginRequiredState.hidden = false;
          this.updateCount(0);
          return;
        }
        if (this.loginRequiredState) this.loginRequiredState.hidden = true;

        const handles = WishlistStore.getAll();
        this.updateCount(handles.length);

        if (handles.length === 0) {
          this._products = [];
          this.grid.innerHTML = "";
          this.grid.hidden = true;
          this.emptyState.hidden = false;
          return;
        }

        Promise.all(
          handles.map((handle) =>
            fetch(`/products/${handle}.js`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        ).then((products) => {
          const current = WishlistStore.getAll();
          this._products = products.filter(
            (p) => p && current.indexOf(p.handle) !== -1
          );

          this.grid.innerHTML = "";
          this._products.forEach((p) => this.grid.appendChild(this.buildCard(p)));

          const hasItems = this.grid.children.length > 0;
          this.grid.hidden = !hasItems;
          this.emptyState.hidden = hasItems;
        });
      }

      addAllToCart() {
        const items = this._products
          .map((p) => p.variants.find((v) => v.available) || p.variants[0])
          .filter((v) => v && v.available)
          .map((v) => ({ id: v.id, quantity: 1 }));

        if (!items.length) return;

        const cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        this.addAllButton.setAttribute("aria-disabled", "true");
        this.addAllButton.classList.add("loading");

        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        fetch(window.routes.cart_add_url, {
          ...fetchConfig("json"),
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (cart && response.sections) {
              cart.renderContents(response);
            }
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "favorites-list" });
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.addAllButton.removeAttribute("aria-disabled");
            this.addAllButton.classList.remove("loading");
          });
      }

      buildCard(product) {
        const variant =
          product.variants.find((v) => v.available) || product.variants[0];

        const item = document.createElement("div");
        item.className = "favorites-list__item";

        const media = document.createElement("a");
        media.href = product.url;
        media.className = "favorites-list__media";
        if (product.featured_image) {
          const img = document.createElement("img");
          img.src = product.featured_image;
          img.alt = product.title;
          img.loading = "lazy";
          media.appendChild(img);
        }
        item.appendChild(media);

        item.appendChild(
          createFavoriteButtonElement(
            product.handle,
            this.dataset.labelAdd,
            this.dataset.labelRemove
          )
        );

        const vendor = document.createElement("p");
        vendor.className = "favorites-list__vendor";
        vendor.textContent = product.vendor;
        item.appendChild(vendor);

        const titleHeading = document.createElement("h3");
        titleHeading.className = "card-heading heading-bold favorites-list__title";
        const title = document.createElement("a");
        title.href = product.url;
        title.className = "full-unstyled-link";
        title.textContent = product.title;
        titleHeading.appendChild(title);
        item.appendChild(titleHeading);

        const ratingBadge = document.createElement("div");
        ratingBadge.className = "jdgm-widget jdgm-preview-badge";
        ratingBadge.dataset.id = product.id;
        ratingBadge.dataset.autoInstall = "false";
        item.appendChild(ratingBadge);

        const price = document.createElement("p");
        price.className = "favorites-list__price";
        price.textContent = this.formatMoney(variant.price);
        item.appendChild(price);

        if (variant.unit_price && variant.unit_price_measurement) {
          const unitPrice = document.createElement("small");
          unitPrice.className = "unit-price caption";
          const measurement = variant.unit_price_measurement;
          const unitLabel =
            measurement.reference_unit === "item"
              ? this.dataset.labelUnitPrice || ""
              : measurement.reference_unit;
          const refValue =
            measurement.reference_value !== 1 ? measurement.reference_value : "";
          unitPrice.textContent = `${this.formatMoney(
            variant.unit_price
          )} / ${refValue}${unitLabel}`;
          item.appendChild(unitPrice);
        }

        const form = document.createElement("product-form");
        const formEl = document.createElement("form");
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "id";
        input.value = variant.id;
        formEl.appendChild(input);

        const submit = document.createElement("button");
        submit.type = "submit";
        submit.className = "button button--full-width";
        const submitLabel = document.createElement("span");
        submitLabel.textContent = this.dataset.labelAddToCart;
        submit.appendChild(submitLabel);
        if (!variant.available) submit.disabled = true;
        formEl.appendChild(submit);

        form.appendChild(formEl);
        item.appendChild(form);

        return item;
      }
    }
  );
}

(function initWishlistHeaderBubble() {
  const bubble = document.querySelector(
    "#wishlist-icon-bubble .wishlist-count-bubble"
  );
  if (!bubble) return;
  const countEl = bubble.querySelector("span[aria-hidden]");

  function render() {
    const count = WishlistStore.isLoggedIn() ? WishlistStore.getAll().length : 0;
    bubble.hidden = count === 0;
    if (countEl) countEl.textContent = count;
  }

  render();
  subscribe(PUB_SUB_EVENTS.wishlistUpdate, render);
})();
